import { TaskOutcomeFieldsEditor } from '../components/cards/task-types/TaskOutcomeFieldsEditor';
import { T, useGT } from 'gt-react';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import type { UnsavedChangesGuard } from 'src/hooks/useUnsavedChangesGuard';
import {
  TaskTypeActionsEditor,
  TaskTypeBehaviorFields,
  TaskTypeIdentityFields,
  TaskTypeInputSchemaField,
  TaskTypeNamespaceField,
  TaskTypeSection,
  TaskTypeStagesEditor,
  type TaskTypeStageOption,
} from '../components/cards/task-types/TaskTypeEditorSections';
import { TaskTypeDefaultAssigneeField } from '../components/cards/task-types/TaskTypeDefaultAssigneeField';
import { TaskTypeViewSection } from '../components/cards/task-types/TaskTypeViewSection';
import { TaskTypeMetricsSection } from '../components/cards/task-types/TaskTypeMetricsSection';
import { TaskTypeFeedsSection } from '../components/cards/task-types/TaskTypeFeedsSection';
import { TaskTypeActivitySection } from '../components/cards/task-types/TaskTypeActivitySection';
import { TaskTypeViewerGroupsField } from '../components/cards/task-types/TaskTypeViewerGroupsField';
import { TaskTypeEscalationTargetField } from '../components/cards/task-types/TaskTypeEscalationTargetField';
import { TaskTypeProceduresEditor } from '../components/cards/task-types/TaskTypeProceduresEditor';
import { useTaskTypeProcedureInput } from '../components/cards/task-types/useTaskTypeProcedureInput';
import { useTaskTypeProcedureHealth } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import type { TaskTypeEditorState } from '../components/cards/task-types/taskTypeAdmin';

/**
 * The full task type form (details, behavior, stages, actions) plus the
 * unsaved-changes prompt — shared by the create and detail pages, which only
 * differ in their header and how the draft is persisted.
 */
export function TaskTypeEditorBody({
  editor,
  setDraft,
  canManage,
  stageOptions,
  keyDisabled,
  onNameChange,
  unsavedGuard,
}: {
  editor: TaskTypeEditorState;
  setDraft: (next: TaskTypeEditorState) => void;
  canManage: boolean;
  stageOptions: TaskTypeStageOption[];
  /** Keys are immutable once a type exists. */
  keyDisabled: boolean;
  /** Override to derive other fields from the name (e.g. the key on create). */
  onNameChange?: (name: string) => void;
  unsavedGuard: UnsavedChangesGuard;
}) {
  const gt = useGT();
  // Recent playbook health, for an existing type only (nothing to report while creating).
  const { data: procedureHealth } = useTaskTypeProcedureHealth(
    editor.editing?.id ?? null,
  );
  const procedureInput = useTaskTypeProcedureInput(editor);

  return (
    <>
      <div className="flex flex-col gap-3 p-2">
        <TaskTypeSection
          title={<T context="task type editor section title">Details</T>}
          description={
            <T context="task type editor details section description">
              Name, description, where new tasks of this type land — their
              namespace and default assignee — who can see the whole queue, and
              who an agent hands them to when it needs help.
            </T>
          }
        >
          <TaskTypeIdentityFields
            keyValue={editor.key}
            name={editor.name}
            description={editor.description}
            keyDisabled={keyDisabled}
            nameFirst
            sideBySide
            disabled={!canManage}
            onKeyChange={(key) => setDraft({ ...editor, key })}
            onNameChange={
              onNameChange ?? ((name) => setDraft({ ...editor, name }))
            }
            onDescriptionChange={(description) =>
              setDraft({ ...editor, description })
            }
          />
          <TaskTypeNamespaceField
            value={editor.taskNamespaceId}
            disabled={!canManage}
            onChange={(taskNamespaceId) =>
              setDraft({ ...editor, taskNamespaceId })
            }
          />
          <TaskTypeDefaultAssigneeField
            editor={editor}
            disabled={!canManage}
            onChange={(next) => setDraft({ ...editor, ...next })}
          />
          <TaskTypeEscalationTargetField
            editor={editor}
            disabled={!canManage}
            onChange={(next) => setDraft({ ...editor, ...next })}
          />
          <TaskTypeViewerGroupsField
            viewerUserGroupIds={editor.viewerUserGroupIds}
            disabled={!canManage}
            onChange={(viewerUserGroupIds) =>
              setDraft({ ...editor, viewerUserGroupIds })
            }
          />
        </TaskTypeSection>

        <TaskTypeSection
          title={<T context="task type editor section title">Behavior</T>}
          description={
            <T context="task type editor behavior section description">
              How this type attaches to records, what callers must pass when
              creating a task, when it can close, and how it can end.
            </T>
          }
        >
          <TaskTypeInputSchemaField
            value={editor.inputSchemaDefinitionId}
            disabled={!canManage}
            onChange={(inputSchemaDefinitionId) =>
              setDraft({ ...editor, inputSchemaDefinitionId })
            }
          />
          <TaskTypeBehaviorFields
            editor={editor}
            disabled={!canManage}
            onChange={setDraft}
          />
        </TaskTypeSection>

        <TaskOutcomeFieldsEditor
          fields={editor.outcomeFields}
          enumId={editor.outcomeEnumId}
          disabled={!canManage}
          onChange={(outcomeFields) => setDraft({ ...editor, outcomeFields })}
        />

        {editor.editing ? (
          <TaskTypeFeedsSection taskTypeKey={editor.editing.key} />
        ) : null}

        <TaskTypeMetricsSection
          drafts={editor.metrics}
          schemaStatus={procedureInput.status}
          inputFields={procedureInput.metricFields}
          anchorEntityType={editor.anchorEntityType}
          disabled={!canManage}
          onChange={(metrics) => setDraft({ ...editor, metrics })}
        />

        <TaskTypeViewSection
          editor={editor}
          disabled={!canManage}
          onViewTextChange={(viewText) => setDraft({ ...editor, viewText })}
        />

        <TaskTypeStagesEditor
          editor={editor}
          stageOptions={stageOptions}
          disabled={!canManage}
          onChange={setDraft}
        />
        <TaskTypeProceduresEditor
          agentId={
            editor.defaultAssigneeMode === 'agent'
              ? editor.defaultAssigneeAgentDefinitionId
              : undefined
          }
          drafts={editor.procedures}
          disabled={!canManage}
          health={procedureHealth ?? null}
          inputFields={procedureInput.fields}
          schemaStatus={procedureInput.status}
          onChange={(procedures) => setDraft({ ...editor, procedures })}
        />
        <TaskTypeActionsEditor
          drafts={editor.actions}
          outcomeEnumId={editor.outcomeEnumId}
          disabled={!canManage}
          onChange={(actions) => setDraft({ ...editor, actions })}
        />

        {editor.editing ? (
          <TaskTypeActivitySection taskTypeId={editor.editing.id} />
        ) : null}
      </div>

      <ConfirmDialog
        open={unsavedGuard.isPrompting}
        onClose={unsavedGuard.cancelLeave}
        onConfirm={unsavedGuard.confirmLeave}
        title={gt('Discard unsaved changes?', {
          context: 'unsaved-changes leave warning dialog title',
        })}
        message={gt(
          'Discard unsaved changes to this task type? Your edits will be lost.',
          {
            context:
              'confirm dialog before leaving the task type editor with unsaved changes',
          },
        )}
        confirmText={gt('Leave without saving', {
          context: 'unsaved-changes leave warning, confirm button',
        })}
        cancelText={gt('Keep editing', {
          context: 'unsaved-changes leave warning, cancel button',
        })}
        dangerous
      />
    </>
  );
}
